'use client';

import { useEffect, useState } from 'react';
import { PLANS_INCLUDE, type PlanOption } from '@content-removal/shared';
import { Icon } from './line-icon';
import { PlanColumns, type PlanChoice } from './plan-columns';

/**
 * Every sold plan, creator or agency behind one switch. The paywall sells both.
 * The plan page can put the public call card on the agency tab instead; the
 * columns themselves stay here.
 */
export function PlanPicker({
  plans,
  current,
  currentInterval,
  cycle,
  busy,
  riskUsd,
  onChoose,
  onKind,
  initialKind = 'creator',
  only,
  showAgencyPlans = true,
}: {
  plans: PlanOption[];
  current?: string | null;
  /** The cycle the account already pays on, so the same plan on the other cycle stays a button. */
  currentInterval?: 'month' | 'year' | null;
  /** Month or year prices. Omitted, the columns stay monthly, as the public page does. */
  cycle?: 'month' | 'year';
  busy?: string | null;
  riskUsd?: number;
  onChoose: (choice: PlanChoice) => void;
  /** The tab now showing, so a yearly switch can leave the screen on Agency. */
  onKind?: (kind: 'creator' | 'agency') => void;
  initialKind?: 'creator' | 'agency';
  /** The account already is one kind: its own plans, and no way to the other's. */
  only?: 'creator' | 'agency';
  /**
   * False hides the agency columns and shows the public call card. The columns
   * stay in PlanColumns, so a later turn can sell them here again.
   */
  showAgencyPlans?: boolean;
}) {
  const [picked, setKind] = useState(initialKind);
  const kind = only ?? picked;
  useEffect(() => { onKind?.(kind); }, [kind, onKind]);
  const yearly = kind === 'creator' && cycle === 'year';
  const agencyCard = kind === 'agency' && !showAgencyPlans;
  return (
    <div className="plan-picker">
      {!only && (
        <div className="segmented plan-picker-kind" role="tablist" aria-label="Plans for">
          {(['creator', 'agency'] as const).map((one) => (
            <button key={one} type="button" role="tab" aria-selected={kind === one} onClick={() => setKind(one)}>
              {one === 'creator' ? 'Creator' : 'Agency'}
            </button>
          ))}
        </div>
      )}
      {agencyCard ? (
        <AgencyOffer />
      ) : (
        <PlanColumns
          plans={plans}
          kind={kind}
          current={current}
          currentInterval={currentInterval}
          cycle={kind === 'agency' ? 'month' : cycle}
          busy={busy}
          riskUsd={riskUsd}
          onChoose={onChoose}
        />
      )}
      {!agencyCard && (
        <p className="plan-picker-note">{PLANS_INCLUDE} Prices in USD, {yearly ? 'billed yearly' : 'monthly'}{kind === 'agency' ? ', plus each profile you add' : ''}.</p>
      )}
    </div>
  );
}

/**
 * The agency offer, when those plans are not on sale. The landing pricing tab
 * and the plan page both render this, so the words and the button stay one card.
 */
export function AgencyOffer() {
  return (
    <div className="plan-agency-offer">
      <span className="plan-agency-offer-title">Agencies &amp; studios</span>
      <p>
        One dashboard for your whole roster — per-creator coverage, member assignments and white-label. We tailor the plan to your roster size, so book a call and we will put together pricing that fits.
      </p>
      <a className="plan-agency-offer-cta" href="/contact">
        Book a Call
        <Icon name="arrow" color="currentColor" size={17} />
      </a>
    </div>
  );
}
